import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { OptimizedImage } from "@/components/ui/optimized-image"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"

import type { SeoMeta } from "@/types"
import { LOCATIONS, LOCATION_SLUGS } from "@/data/directory/registry"
import { rankOffices } from "@/data/rankings/scoring"
import type { RankedOffice } from "@/data/rankings/scoring"
import { LOCATION_IMAGES, imageSrcSet } from "@/data/location-images"
import { cdnUrl } from "@/lib/cdn"

/* ------------------------------------------------------------------ */
/* Medal colours                                                       */
/* ------------------------------------------------------------------ */

const MEDAL_COLOURS: Record<number, { bg: string; text: string; border: string }> = {
  1: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200" },
  2: { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-200" },
  3: { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-200" },
}

/* ------------------------------------------------------------------ */
/* Star rating                                                         */
/* ------------------------------------------------------------------ */

function StarRating({ rating, reviewCount }: { rating: number; reviewCount: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          viewBox="0 0 20 20"
          fill={star <= Math.round(rating) ? "#C41E2A" : "#E8E4DF"}
          className="size-4"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 0 0 .95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 0 0-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 0 0-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 0 0-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 0 0 .951-.69l1.07-3.292Z" />
        </svg>
      ))}
      <span className="ml-1 text-sm font-medium text-charcoal">{rating.toFixed(1)}</span>
      <span className="text-sm text-warm-grey">({reviewCount})</span>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Office favicon                                                      */
/* ------------------------------------------------------------------ */

function OfficeLogo({ office, size = "md" }: { office: RankedOffice; size?: "sm" | "md" }) {
  const sizeClasses = size === "sm" ? "size-8 text-xs" : "size-12 text-sm"
  const domain = (() => {
    try {
      return new URL(
        office.website.startsWith("http")
          ? office.website
          : `https://${office.website}`
      ).hostname
    } catch {
      return null
    }
  })()

  const initials = office.name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()

  if (domain) {
    return (
      <img
        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
        alt=""
        className={`${sizeClasses} shrink-0 rounded-lg bg-stone object-contain`}
        onError={(e) => {
          const target = e.target as HTMLImageElement
          target.style.display = "none"
          target.nextElementSibling?.classList.remove("hidden")
        }}
      />
    )
  }

  return (
    <div
      className={`${sizeClasses} flex shrink-0 items-center justify-center rounded-lg bg-stone font-semibold text-charcoal`}
    >
      {initials}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Ranked office card                                                  */
/* ------------------------------------------------------------------ */

function RankedOfficeCard({
  office,
  locationSlug,
}: {
  office: RankedOffice
  locationSlug: string
}) {
  const medal = MEDAL_COLOURS[office.rank]
  const currentYear = new Date().getFullYear()

  return (
    <div
      className={`flex gap-4 rounded-xl border bg-white p-5 transition-all hover:shadow-sm sm:gap-6 sm:p-6 ${
        medal ? medal.border : "border-stone"
      }`}
    >
      {/* Rank */}
      <div className="flex flex-col items-center">
        <div
          className={`flex size-10 shrink-0 items-center justify-center rounded-full font-display text-lg font-bold ${
            medal
              ? `${medal.bg} ${medal.text}`
              : "bg-stone/50 text-warm-grey"
          }`}
        >
          {office.rank}
        </div>
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <OfficeLogo office={office} />
          <div className="hidden" />
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg font-bold text-charcoal">
              {office.name}
            </h3>
            <p className="text-sm text-slate">{office.branch}</p>
          </div>
        </div>

        <div className="mt-3">
          <StarRating rating={office.rating} reviewCount={office.reviewCount} />
        </div>

        {/* Badges */}
        {office.badges.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {office.badges.map((badge) => (
              <span
                key={badge}
                className="rounded-full bg-red/8 px-2.5 py-0.5 text-[11px] font-medium text-red"
              >
                {badge}
              </span>
            ))}
          </div>
        )}

        {/* Stats row */}
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-warm-grey">
          <span>Est. {office.established}</span>
          <span>~{office.propertyCount} listings</span>
          <span>Avg. {office.avgPrice}</span>
          {currentYear - office.established >= 10 && (
            <span>{currentYear - office.established} years</span>
          )}
        </div>

        {/* Actions */}
        <div className="mt-4 flex items-center gap-4">
          <Link
            href={`/directory/${locationSlug}/${office.slug}`}
            className="text-sm font-medium text-red hover:text-deep-red"
          >
            View profile
          </Link>
          <a
            href={office.website.startsWith("http") ? office.website : `https://${office.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-sm text-slate hover:text-charcoal"
          >
            Website
            <svg viewBox="0 0 20 20" fill="currentColor" className="size-3.5">
              <path
                fillRule="evenodd"
                d="M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Zm7.25-.75a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0V6.31l-5.47 5.47a.75.75 0 1 1-1.06-1.06l5.47-5.47H12.25a.75.75 0 0 1-.75-.75Z"
                clipRule="evenodd"
              />
            </svg>
          </a>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* SEO copy per location                                               */
/* ------------------------------------------------------------------ */

const SEO_COPY: Record<string, { heading: string; paragraphs: string[] }> = {
  bristol: {
    heading: "Finding the best estate agent in Bristol",
    paragraphs: [
      "Bristol's property market is one of the most dynamic in the South West, with strong demand across Clifton, Redland, Bishopston, and the harbourside. The right agent can make a significant difference to your sale price and time on market.",
      "Our rankings consider the full picture — not just star ratings, but the volume of reviews, years of local experience, and current market activity. Whether you're selling a Georgian townhouse in Clifton or a family home in Southville, the agents at the top of this list have proven track records.",
    ],
  },
  brighton: {
    heading: "Finding the best estate agent in Brighton",
    paragraphs: [
      "Brighton and Hove's property market is fast-moving and fiercely competitive, with agents serving areas from the seafront to the South Downs. The best agents here combine local knowledge with strong marketing — essential in a market where presentation matters.",
      "Our rankings balance review quality, volume, experience, and current listings to surface the agents who consistently deliver for their clients. From Kemptown character homes to modern Hove apartments, the top-ranked agents here know how to sell in Brighton.",
    ],
  },
  bath: {
    heading: "Finding the best estate agent in Bath",
    paragraphs: [
      "Bath's property market is defined by its period architecture, conservation areas, and premium price points. Selling a Georgian property requires an agent who understands both the heritage value and the modern buyer — and the best agents in Bath deliver on both.",
      "Our rankings reflect an agent's overall market presence: quality of service (ratings), depth of experience (reviews), established track record (years in business), and current activity (listings). The agents ranked highest here have earned their position over time.",
    ],
  },
  oxford: {
    heading: "Finding the best estate agent in Oxford",
    paragraphs: [
      "Oxford's property market is shaped by the university, strong international demand, and a mix of period and modern housing across areas like Summertown, Jericho, and Headington. The best agents here understand both the academic letting market and residential sales.",
      "Our rankings combine four key metrics to give a balanced picture of each agent's standing. Whether you're selling a North Oxford family home or a Jericho terrace, the top-ranked agents have the reviews, experience, and market activity to back up their reputation.",
    ],
  },
}

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function BestLocation({
  meta,
  location,
}: {
  meta: SeoMeta
  location: string
}) {
  const locationData = LOCATIONS[location]
  if (!locationData) return null

  const rankedOffices = rankOffices(locationData.offices)
  const otherLocations = LOCATION_SLUGS.filter((s) => s !== location)
  const seoCopy = SEO_COPY[location]

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        {/* Structured Data — ItemList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `Best Estate Agents in ${locationData.name} 2026`,
            description: meta.description,
            numberOfItems: rankedOffices.length,
            itemListOrder: "https://schema.org/ItemListOrderDescending",
            itemListElement: rankedOffices.map((o) => ({
              "@type": "ListItem",
              position: o.rank,
              item: {
                "@type": "RealEstateAgent",
                name: `${o.name} — ${o.branch}`,
                address: {
                  "@type": "PostalAddress",
                  streetAddress: o.address,
                  addressLocality: locationData.name,
                  postalCode: o.postcode,
                  addressCountry: "GB",
                },
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: o.rating,
                  reviewCount: o.reviewCount,
                  bestRating: 5,
                },
              },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Best Estate Agents", path: "/best" }, { name: locationData.name }]} />
      </Head>

      {/* Breadcrumb */}
      <nav className="border-b border-stone px-6 py-3">
        <div className="mx-auto max-w-4xl">
          <ol className="flex items-center gap-2 text-xs text-warm-grey">
            <li>
              <Link href="/best" className="hover:text-charcoal">
                Best Estate Agents
              </Link>
            </li>
            <li>/</li>
            <li className="font-medium text-charcoal">{locationData.name}</li>
          </ol>
        </div>
      </nav>

      {/* City hero image */}
      {LOCATION_IMAGES[location] && (
        <div className="relative h-64 w-full overflow-hidden sm:h-80 md:h-96">
          <OptimizedImage
            eager
            src={cdnUrl(LOCATION_IMAGES[location].url)}
            srcSet={imageSrcSet(LOCATION_IMAGES[location].url)}
            sizes="100vw"
            alt={LOCATION_IMAGES[location].alt}
            className="h-full w-full object-cover"
            width={1200}
            height={510}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 px-6 pb-8">
            <div className="mx-auto max-w-4xl">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/80">
                Rankings 2026
              </p>
              <h1 className="mt-2 font-display text-3xl font-bold text-white sm:text-4xl">
                Best estate agents in {locationData.name}
              </h1>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="px-6 py-20 md:py-28">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full bg-red/8 px-3 py-1">
            <span className="text-[10px] font-medium uppercase tracking-[0.15em] text-red">
              Updated 2026
            </span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Best estate agents in {locationData.name}<span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            {rankedOffices.length} agents ranked by reviews, experience, and
            local market expertise. Updated for 2026.
          </p>

          {/* Other locations */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs text-warm-grey">Also available:</span>
            {otherLocations.map((slug) => (
              <Link
                key={slug}
                href={`/best/${slug}`}
                className="rounded-full border border-stone bg-white px-3 py-1 text-xs font-medium text-slate transition-colors hover:border-red hover:text-red"
              >
                {LOCATIONS[slug]?.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Ranked list */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <div className="space-y-4">
            {rankedOffices.map((office) => (
              <RankedOfficeCard
                key={office.slug}
                office={office}
                locationSlug={location}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            How we rank agents<span className="text-red">.</span>
          </h2>
          <div className="mt-6 space-y-4 text-sm leading-relaxed text-slate">
            <p>
              Each office is scored using a weighted composite of four publicly
              available metrics: customer rating (40%), review volume (25%),
              years established (20%), and active listing count (15%). The
              weighting prioritises service quality while also crediting
              consistency and market presence.
            </p>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                { label: "Rating", weight: "40%", colour: "bg-red" },
                { label: "Reviews", weight: "25%", colour: "bg-red/70" },
                { label: "Experience", weight: "20%", colour: "bg-red/50" },
                { label: "Listings", weight: "15%", colour: "bg-red/30" },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border border-stone bg-cream/50 p-3 text-center">
                  <div className={`mx-auto h-1.5 w-12 rounded-full ${item.colour}`} />
                  <p className="mt-2 text-xs font-semibold text-charcoal">{item.label}</p>
                  <p className="text-lg font-bold text-charcoal">{item.weight}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Compare CTA */}
      <section className="border-t border-stone bg-cream px-6 py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-2xl font-bold text-charcoal md:text-3xl">
            Want to compare agents side by side<span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-base text-slate">
            Use our directory to select up to six {locationData.name} agents and
            compare ratings, specialisms, areas, and more.
          </p>
          <Link
            href={`/directory/${location}`}
            className="mt-6 inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
          >
            Compare {locationData.name} agents
          </Link>
        </div>
      </section>

      {/* SEO copy */}
      {seoCopy && (
        <section className="border-t border-stone px-6 py-20">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-display text-2xl font-bold text-charcoal">
              {seoCopy.heading}<span className="text-red">.</span>
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-slate">
              {seoCopy.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            Are you a {locationData.name} estate agent<span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-warm-grey">
            Turn your property listings into scroll-stopping video ads. Paste a
            listing, pick a style, and publish across every social platform in
            under a minute.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Request a demo
            </Link>
            <Link
              href="/how-it-works"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-6 py-3 text-base font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              See how it works
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
